// src/pages/eglises/ModifierEglise.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  /* Card */
  .card-modern {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s;
  }
  .card-modern:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .card-modern.border-primary {
    border-top-color: ${COLORS.primary};
  }

  .card-header-modern {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.8rem 1.2rem;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .card-header-modern i {
    color: ${COLORS.secondary};
  }

  .card-body-modern {
    padding: 1.5rem;
  }

  /* Stepper */
  .stepper-wrapper {
    padding: 0.5rem 0;
  }

  .stepper-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
    flex: 1;
  }

  .step-circle {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background: #e9edf2;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.85rem;
    margin-bottom: 6px;
    transition: all 0.3s ease;
  }

  .step.completed .step-circle {
    background: ${COLORS.primary};
    color: white;
  }

  .step.active .step-circle {
    background: ${COLORS.primary};
    color: white;
    box-shadow: 0 0 0 3px ${COLORS.primary}40, 0 4px 10px rgba(41,90,167,0.2);
    transform: scale(1.05);
  }

  .step-label {
    font-size: 0.65rem;
    font-weight: 500;
    color: #94a3b8;
    text-align: center;
    transition: all 0.3s ease;
  }

  .step.active .step-label {
    color: ${COLORS.primary};
    font-weight: 600;
  }

  .step.completed .step-label {
    color: ${COLORS.primary};
  }

  .step-connector {
    flex: 1;
    height: 2px;
    background: #e9edf2;
    margin: 0 8px;
    position: relative;
    top: -10px;
    border-radius: 2px;
    transition: all 0.3s ease;
  }

  .step-connector.completed {
    background: ${COLORS.primary};
  }

  .progress-modern {
    height: 3px;
    border-radius: 2px;
    background: #e9edf2;
    overflow: hidden;
    margin-top: 0.5rem;
  }

  .progress-modern .progress-bar {
    background: ${COLORS.primary};
    transition: width 0.5s ease;
  }

  /* Form */
  .form-label-modern {
    font-size: 0.7rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.2rem;
  }
  .form-label-modern .required {
    color: ${COLORS.danger};
    margin-left: 2px;
  }

  .form-control-modern, .form-select-modern {
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.4rem 0.7rem;
    font-size: 0.85rem;
    transition: all 0.2s ease;
    background: white;
  }
  .form-control-modern:focus, .form-select-modern:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 3px rgba(41,90,167,0.08);
  }

  /* Quill */
  .quill-wrapper {
    background: white;
    border-radius: 4px;
    overflow: hidden;
  }
  .quill-wrapper .ql-container {
    min-height: 120px;
    font-size: 0.85rem;
    border: 1px solid #e2e8f0;
    border-top: none;
  }
  .quill-wrapper .ql-toolbar {
    border: 1px solid #e2e8f0;
    border-radius: 4px 4px 0 0;
    background: #f8fafc;
  }
  .quill-wrapper .ql-editor {
    min-height: 120px;
  }

  /* Upload */
  .upload-area {
    border: 2px dashed #e2e8f0;
    border-radius: 4px;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fafbfc;
  }
  .upload-area:hover {
    border-color: ${COLORS.primary};
    background: rgba(41,90,167,0.02);
  }
  .upload-area.has-image {
    padding: 0.5rem;
  }
  .upload-icon {
    font-size: 2.8rem;
    color: ${COLORS.primary};
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }
  .upload-text {
    color: #1e293b;
    font-size: 0.9rem;
    font-weight: 500;
  }
  .upload-hint {
    color: #94a3b8;
    font-size: 0.75rem;
  }
  .upload-preview {
    max-width: 200px;
    max-height: 180px;
    border-radius: 4px;
    object-fit: contain;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
  .preview-wrapper {
    position: relative;
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
  }
  .preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.4);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .preview-wrapper:hover .preview-overlay {
    opacity: 1;
  }
  .btn-change {
    border-radius: 4px;
    padding: 0.3rem 1rem;
    font-size: 0.8rem;
    font-weight: 500;
    border: none;
    background: white;
    color: #1e293b;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
  .btn-change:hover {
    background: #f1f5f9;
  }

  /* Caisse switch */
  .caisse-switch-container {
    background: #f8fafc;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #e2e8f0;
  }
  .caisse-switch {
    font-size: 0.85rem;
  }
  .caisse-switch .form-check-input:checked {
    background-color: ${COLORS.primary};
    border-color: ${COLORS.primary};
  }
  .caisse-badge {
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
  }
  .caisse-badge.active {
    background: ${COLORS.success}15;
    color: ${COLORS.success};
    border: 1px solid ${COLORS.success}30;
  }
  .caisse-badge.inactive {
    background: #e9edf2;
    color: #64748b;
    border: 1px solid #d1d5db;
  }

  /* Notifications */
  .toast-notification {
    padding: 0.8rem 1rem;
    border: none;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 1.5rem;
    animation: slideDown 0.3s ease-out;
    border-radius: 4px;
  }
  .toast-notification-success {
    background: #f0fdf4;
    border-left: 3px solid ${COLORS.success};
  }
  .toast-notification-error {
    background: #fef2f2;
    border-left: 3px solid ${COLORS.danger};
  }
  .toast-icon {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1rem;
  }
  .toast-icon-success {
    background: rgba(16,185,129,0.15);
    color: ${COLORS.success};
  }
  .toast-icon-error {
    background: rgba(239,68,68,0.15);
    color: ${COLORS.danger};
  }
  .toast-content {
    flex: 1;
  }
  .toast-title {
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 1px;
  }
  .toast-message {
    font-size: 0.75rem;
    color: #64748b;
    margin: 0;
  }
  .toast-close {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 2px;
    font-size: 1rem;
  }

  .step-content {
    min-height: 300px;
  }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Animations */
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
    .step-label {
      font-size: 0.55rem;
    }
    .step-circle {
      width: 30px;
      height: 30px;
      font-size: 0.7rem;
    }
    .card-body-modern {
      padding: 1rem;
    }
    .caisse-switch-container {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
  }
`;

export default function ModifierEglise() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const fileInputRef = useRef(null);

  const [egliseData, setEgliseData] = useState({
    id: '',
    matricule: '',
    nom: '',
    tel: '',
    email: '',
    province: '',
    ville: '',
    adresse: '',
    date_created: '',
    caisse: 0,
    logo: ''
  });

  const [adminData, setAdminData] = useState({
    id: '',
    matricule: '',
    nom: '',
    prenom: '',
    datenaiss: '',
    lieunaiss: '',
    contact: '',
    email: '',
    role: 'Administrateur',
    gendre: 'Homme'
  });

  const [bienvenueData, setBienvenueData] = useState({
    titre: '',
    description: '',
    vision: '',
    mission: '',
    action: '',
    culte: '',
    reference: '',
    chapitre: ''
  });

  const [formData, setFormData] = useState({
    logo: null
  });

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link'
  ];

  useEffect(() => {
    fetchEgliseData();

    return () => {
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [id]);

  const fetchEgliseData = async () => {
    try {
      setFetchLoading(true);

      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const response = await fetch(`https://josephsbusiness.com/api/eglises_details.php?user_id=${userId}&id=${id}&_=${Date.now()}`, {
        headers: { 'Accept': 'application/json' }
      });

      const result = await response.json();

      if (result.success) {
        const eglise = result.eglise;

        setEgliseData({
          id: eglise.id || '',
          matricule: eglise.matricule || '',
          nom: eglise.nom || '',
          tel: eglise.tel || '',
          email: eglise.email || '',
          province: eglise.province || '',
          ville: eglise.ville || '',
          adresse: eglise.adresse || '',
          date_created: eglise.date_created || '',
          caisse: parseInt(eglise.caisse) || 0,
          logo: eglise.logo || ''
        });

        if (eglise.logo && eglise.logo !== 'R.jpg') {
          setPreviewImage(`https://josephsbusiness.com/assets/images/${eglise.logo}`);
        }

        if (result.admin) {
          const admin = result.admin;
          setAdminData({
            id: admin.id || '',
            matricule: admin.matricule || '',
            nom: admin.nom || '',
            prenom: admin.prenom || '',
            datenaiss: admin.datenaiss || '',
            lieunaiss: admin.lieunaiss || '',
            contact: admin.contact || '',
            email: admin.email || '',
            role: admin.role || 'Administrateur',
            gendre: admin.gendre || 'Homme'
          });
        }

        if (result.bienvenue) {
          setBienvenueData({
            titre: result.bienvenue.titre || '',
            description: result.bienvenue.description || '',
            vision: result.bienvenue.vision || '',
            mission: result.bienvenue.mission || '',
            action: result.bienvenue.action || '',
            culte: result.bienvenue.culte || '',
            reference: result.bienvenue.reference || '',
            chapitre: result.bienvenue.chapitre || ''
          });
        }
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleEgliseChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEgliseData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleAdminChange = (e) => {
    const { name, value } = e.target;
    setAdminData(prev => ({ ...prev, [name]: value }));
  };

  const handleBienvenueChange = (name, value) => {
    setBienvenueData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Le fichier est trop volumineux. Taille maximum: 5MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setError('Type de fichier non autorisé. Formats acceptés: JPG, PNG, WEBP');
        return;
      }
      setFormData(prev => ({ ...prev, logo: file }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const removeLogo = () => {
    if (previewImage && previewImage.startsWith('blob:')) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
    setFormData(prev => ({ ...prev, logo: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      if (!egliseData.nom || !egliseData.ville || !egliseData.date_created) {
        setError('Nom, ville et date de création sont requis');
        setLoading(false);
        return;
      }

      const submitData = new FormData();

      submitData.append('id_eglise', egliseData.id);
      submitData.append('id_admin', adminData.id);

      submitData.append('eglise_nom', egliseData.nom || '');
      submitData.append('eglise_tel', egliseData.tel || '');
      submitData.append('eglise_email', egliseData.email || '');
      submitData.append('eglise_province', egliseData.province || '');
      submitData.append('eglise_ville', egliseData.ville || '');
      submitData.append('eglise_adresse', egliseData.adresse || '');
      submitData.append('eglise_date_at', egliseData.date_created || '');
      submitData.append('eglise_caisse', egliseData.caisse.toString());

      if (formData.logo) {
        submitData.append('files', formData.logo);
      }

      submitData.append('admin_matricule', adminData.matricule || '');
      submitData.append('admin_nom', adminData.nom || '');
      submitData.append('admin_prenom', adminData.prenom || '');
      submitData.append('admin_datenaiss', adminData.datenaiss || '0000-00-00');
      submitData.append('admin_lieunaiss', adminData.lieunaiss || '');
      submitData.append('admin_contact', adminData.contact || '');
      submitData.append('admin_email', adminData.email || '');
      submitData.append('admin_status', adminData.role || 'Administrateur');
      submitData.append('admin_gendre', adminData.gendre || 'Homme');

      submitData.append('bienvenue_titre', bienvenueData.titre || '');
      submitData.append('bienvenue_description', bienvenueData.description || '');
      submitData.append('bienvenue_vision', bienvenueData.vision || '');
      submitData.append('bienvenue_mission', bienvenueData.mission || '');
      submitData.append('bienvenue_action', bienvenueData.action || '');
      submitData.append('bienvenue_culte', bienvenueData.culte || '');
      submitData.append('bienvenue_reference', bienvenueData.reference || '');
      submitData.append('bienvenue_chapitre', bienvenueData.chapitre || '');

      const response = await fetch(`https://josephsbusiness.com/api/eglises_update.php?user_id=${userId}`, {
        method: 'POST',
        body: submitData
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Réponse non-OK:', text);
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setSuccess('Église mise à jour avec succès');
        setTimeout(() => {
          window.location.href = `/eglises/modifier/${id}?refresh=${Date.now()}`;
        }, 1500);
      } else {
        setError(result.message || 'Erreur lors de la mise à jour');
        setLoading(false);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message || 'Erreur de connexion au serveur');
      setLoading(false);
    }
  };

  const nextStep = (e) => {
    e.preventDefault();
    if (currentStep === 1) {
      if (!egliseData.nom || !egliseData.ville || !egliseData.date_created) {
        setError('Veuillez remplir tous les champs obligatoires (Nom, Ville, Date de création)');
        return;
      }
    } else if (currentStep === 3) {
      if (!adminData.nom || !adminData.prenom || !adminData.email) {
        setError('Veuillez remplir tous les champs obligatoires de l\'administrateur');
        return;
      }
    }
    setError('');
    setCurrentStep(prev => Math.min(prev + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = (e) => {
    e.preventDefault();
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

if (fetchLoading) {
  return (
    <>
      <Header />
      <div
        className="main main-app p-3 p-lg-4"
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #eef2f6 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div className="text-center" style={{ maxWidth: '420px', padding: '2rem' }}>
          {/* Logo ou icône animée */}
          <div className="loading-logo-wrapper mb-4">
            <div className="loading-logo-pulse">
              <div className="loading-logo-circle">
                <i className="ri-building-line" style={{ fontSize: '2.5rem', color: COLORS.primary }}></i>
              </div>
            </div>
          </div>

          {/* Titre avec animation */}
          <h5 className="fw-bold mb-2" style={{ color: COLORS.primaryDark }}>
            Chargement en cours
            <span className="loading-dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </h5>
          <p className="text-muted small mb-4">Récupération des données, veuillez patienter...</p>

          {/* Barre de progression animée */}
          <div className="loading-progress-wrapper mx-auto" style={{ width: '80%' }}>
            <div
              className="loading-progress-track"
              style={{
                height: '4px',
                borderRadius: '4px',
                background: '#e9edf2',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <div
                className="loading-progress-bar"
                style={{
                  height: '100%',
                  width: '0%',
                  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                  backgroundSize: '200% 100%',
                  borderRadius: '4px',
                  animation: 'loadingProgress 2s ease-in-out infinite, shimmer 1.5s linear infinite'
                }}
              />
            </div>
          </div>

          {/* Petit texte de statut */}
          <div className="mt-3">
            <span
              className="badge"
              style={{
                background: `${COLORS.primary}10`,
                color: COLORS.primary,
                borderRadius: '4px',
                padding: '0.3rem 1rem',
                fontSize: '0.7rem',
                fontWeight: '500',
                border: `1px solid ${COLORS.primary}20`
              }}
            >
              <i className="ri-loader-4-line me-1" style={{ animation: 'spin 1s linear infinite' }}></i>
              Préparation...
            </span>
          </div>
        </div>
      </div>

      <style>{`
        /* Animations */
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes loadingProgress {
          0% { width: 0%; }
          50% { width: 70%; }
          100% { width: 100%; }
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @keyframes pulseGlow {
          0%, 100% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        .loading-logo-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .loading-logo-pulse {
          animation: pulseGlow 1.8s ease-in-out infinite;
        }

        .loading-logo-circle {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 32px rgba(41, 90, 167, 0.12);
          border: 2px solid ${COLORS.primary}20;
          animation: float 2.5s ease-in-out infinite;
          position: relative;
        }

        .loading-logo-circle::after {
          content: '';
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          border-radius: 50%;
          border: 2px solid transparent;
          border-top-color: ${COLORS.primary};
          border-right-color: ${COLORS.secondary};
          animation: spin 1.2s linear infinite;
        }

        .loading-dots span {
          display: inline-block;
          animation: bounce 1.4s ease-in-out infinite;
          font-size: 1.6rem;
          line-height: 1;
          margin-left: 1px;
        }
        .loading-dots span:nth-child(1) { animation-delay: 0s; }
        .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
        .loading-dots span:nth-child(3) { animation-delay: 0.4s; }

        @keyframes bounce {
          0%, 80%, 100% { transform: scale(1); opacity: 0.3; }
          40% { transform: scale(1.2); opacity: 1; }
        }

        .loading-progress-track {
          box-shadow: inset 0 1px 3px rgba(0,0,0,0.05);
        }
      `}</style>

      <Footer />
    </>
  );
}


  return (
    <>
      <style>{styles}</style>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-edit-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Mise à jour de l'église</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-building-line"></i> {egliseData.nom || 'Église'}
                  </span>
                  <span className="badge-modern">
                    <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button size="sm" className="btn-modern-outline" onClick={() => navigate('/eglises/sieges')}>
                <i className="ri-arrow-left-line"></i> Retour
              </Button>
            </div>
          </div>

          {/* ===== NOTIFICATIONS ===== */}
          {error && (
            <div className="toast-notification toast-notification-error mb-4">
              <div className="toast-icon toast-icon-error">
                <i className="ri-error-warning-line"></i>
              </div>
              <div className="toast-content">
                <div className="toast-title" style={{ color: COLORS.danger }}>Erreur</div>
                <p className="toast-message">{error}</p>
              </div>
              <button className="toast-close" onClick={() => setError('')}>
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {success && (
            <div className="toast-notification toast-notification-success mb-4">
              <div className="toast-icon toast-icon-success">
                <i className="ri-checkbox-circle-line"></i>
              </div>
              <div className="toast-content">
                <div className="toast-title" style={{ color: COLORS.success }}>Succès</div>
                <p className="toast-message">{success}</p>
              </div>
              <button className="toast-close" onClick={() => setSuccess('')}>
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* ===== CARD PRINCIPALE ===== */}
          <Row className="justify-content-center">
            <Col md={11}>
              <Card className="card-modern border-primary">
                <Card.Header className="card-header-modern">
                  <i className="ri-information-line"></i> Formulaire de modification
                </Card.Header>
                <Card.Body className="card-body-modern">

                  {/* Stepper */}
                  <div className="stepper-wrapper mb-4">
                    <div className="stepper-container">
                      {[1, 2, 3, 4].map((step) => (
                        <React.Fragment key={step}>
                          <div className={`step ${currentStep >= step ? 'completed' : ''} ${currentStep === step ? 'active' : ''}`}>
                            <div className="step-circle">
                              {step === 1 && <i className="ri-building-line"></i>}
                              {step === 2 && <i className="ri-image-line"></i>}
                              {step === 3 && <i className="ri-user-settings-line"></i>}
                              {step === 4 && <i className="ri-file-text-line"></i>}
                            </div>
                            <div className="step-label">
                              {step === 1 && 'Infos église'}
                              {step === 2 && 'Logo'}
                              {step === 3 && 'Administrateur'}
                              {step === 4 && 'Page accueil'}
                            </div>
                          </div>
                          {step < 4 && (
                            <div className={`step-connector ${currentStep > step ? 'completed' : ''}`}></div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="progress-modern">
                      <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                    </div>
                  </div>

                  {/* Formulaire */}
                  <Form onSubmit={handleSubmit}>

                    {/* ÉTAPE 1 - Informations de l'église */}
                    {currentStep === 1 && (
                      <div className="step-content fade-in">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-information-line me-2"></i> Informations générales
                        </h6>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="nom">
                              <Form.Label className="form-label-modern">
                                Nom <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="nom"
                                value={egliseData.nom}
                                onChange={handleEgliseChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="tel">
                              <Form.Label className="form-label-modern">Téléphone</Form.Label>
                              <Form.Control
                                type="text"
                                name="tel"
                                value={egliseData.tel}
                                onChange={handleEgliseChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="email">
                              <Form.Label className="form-label-modern">Email</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={egliseData.email}
                                onChange={handleEgliseChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="province">
                              <Form.Label className="form-label-modern">Province</Form.Label>
                              <Form.Select
                                name="province"
                                value={egliseData.province}
                                onChange={handleEgliseChange}
                                className="form-select-modern"
                              >
                                <option value="">Sélectionner une province</option>
                                <option value="Estuaire">Estuaire</option>
                                <option value="Haut-Ogooué">Haut-Ogooué</option>
                                <option value="Moyen-Ogooué">Moyen-Ogooué</option>
                                <option value="Ngounié">Ngounié</option>
                                <option value="Nyanga">Nyanga</option>
                                <option value="Ogooué-Ivindo">Ogooué-Ivindo</option>
                                <option value="Ogooué-Lolo">Ogooué-Lolo</option>
                                <option value="Ogooué-Maritime">Ogooué-Maritime</option>
                                <option value="Woleu-Ntem">Woleu-Ntem</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="ville">
                              <Form.Label className="form-label-modern">
                                Ville <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="ville"
                                value={egliseData.ville}
                                onChange={handleEgliseChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="adresse">
                              <Form.Label className="form-label-modern">Adresse</Form.Label>
                              <Form.Control
                                type="text"
                                name="adresse"
                                value={egliseData.adresse}
                                onChange={handleEgliseChange}
                                className="form-control-modern"
                                placeholder="Quartier, rue, numéro..."
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="date_created">
                              <Form.Label className="form-label-modern">
                                Date de création <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="date_created"
                                value={egliseData.date_created}
                                onChange={handleEgliseChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="caisse">
                              <Form.Label className="form-label-modern">Caisse nationale ?</Form.Label>
                              <div className="caisse-switch-container">
                                <Form.Check
                                  type="switch"
                                  id="caisse-switch"
                                  name="caisse"
                                  label={egliseData.caisse === 1 ? "Oui" : "Non"}
                                  checked={egliseData.caisse === 1}
                                  onChange={handleEgliseChange}
                                  className="caisse-switch"
                                />
                                <span className={`caisse-badge ${egliseData.caisse === 1 ? 'active' : 'inactive'}`}>
                                  {egliseData.caisse === 1 ? (
                                    <><i className="ri-checkbox-circle-line me-1"></i> Caisse nationale</>
                                  ) : (
                                    <><i className="ri-close-circle-line me-1"></i> Église locale</>
                                  )}
                                </span>
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* ÉTAPE 2 - Logo */}
                    {currentStep === 2 && (
                      <div className="step-content fade-in text-center">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-image-line me-2"></i> Logo de l'église
                        </h6>
                        <p className="text-muted small mb-4">Téléchargez un nouveau logo ou conservez l'actuel.</p>
                        <Row className="justify-content-center">
                          <Col md={8}>
                            <div className="upload-container">
                              <div
                                className={`upload-area ${previewImage ? 'has-image' : ''}`}
                                onClick={() => fileInputRef.current?.click()}
                              >
                                {previewImage ? (
                                  <div className="preview-wrapper">
                                    <img src={previewImage} alt="Logo" className="upload-preview" />
                                    <div className="preview-overlay">
                                      <button
                                        type="button"
                                        className="btn-change"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fileInputRef.current?.click();
                                        }}
                                      >
                                        <i className="ri-edit-line me-1"></i> Changer
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <i className="ri-image-add-line upload-icon"></i>
                                    <p className="upload-text">Cliquez pour télécharger un logo</p>
                                    <span className="upload-hint">JPG, PNG, WEBP (max. 5 Mo)</span>
                                  </>
                                )}
                              </div>

                              <input
                                type="file"
                                ref={fileInputRef}
                                accept=".jpg,.png,.webp,image/jpeg,image/png,image/webp"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                              />

                              {previewImage && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="mt-2 text-danger"
                                  style={{ textDecoration: 'none' }}
                                  onClick={removeLogo}
                                >
                                  <i className="ri-delete-bin-line me-1"></i> Supprimer le logo
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* ÉTAPE 3 - Administrateur */}
                    {currentStep === 3 && (
                      <div className="step-content fade-in">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-user-settings-line me-2"></i> Informations de l'administrateur
                        </h6>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="admin_matricule">
                              <Form.Label className="form-label-modern">Matricule</Form.Label>
                              <Form.Control
                                type="text"
                                name="matricule"
                                value={adminData.matricule}
                                onChange={handleAdminChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="admin_nom">
                              <Form.Label className="form-label-modern">
                                Nom <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="nom"
                                value={adminData.nom}
                                onChange={handleAdminChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="admin_prenom">
                              <Form.Label className="form-label-modern">
                                Prénom <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="prenom"
                                value={adminData.prenom}
                                onChange={handleAdminChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="admin_datenaiss">
                              <Form.Label className="form-label-modern">Date de naissance</Form.Label>
                              <Form.Control
                                type="date"
                                name="datenaiss"
                                value={adminData.datenaiss}
                                onChange={handleAdminChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="admin_lieunaiss">
                              <Form.Label className="form-label-modern">Lieu de naissance</Form.Label>
                              <Form.Control
                                type="text"
                                name="lieunaiss"
                                value={adminData.lieunaiss}
                                onChange={handleAdminChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="admin_contact">
                              <Form.Label className="form-label-modern">Contact</Form.Label>
                              <Form.Control
                                type="text"
                                name="contact"
                                value={adminData.contact}
                                onChange={handleAdminChange}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="admin_email">
                              <Form.Label className="form-label-modern">
                                Email <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={adminData.email}
                                onChange={handleAdminChange}
                                required
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="admin_gendre">
                              <Form.Label className="form-label-modern">Genre</Form.Label>
                              <Form.Select
                                name="gendre"
                                value={adminData.gendre}
                                onChange={handleAdminChange}
                                className="form-select-modern"
                              >
                                <option value="Homme">Homme</option>
                                <option value="Femme">Femme</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group controlId="admin_role">
                              <Form.Label className="form-label-modern">Rôle</Form.Label>
                              <Form.Control
                                type="text"
                                value="Administrateur"
                                disabled
                                className="form-control-modern bg-light"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* ÉTAPE 4 - Page de bienvenue */}
                    {currentStep === 4 && (
                      <div className="step-content fade-in">
                        <h6 className="fw-bold mb-3" style={{ color: COLORS.primaryDark }}>
                          <i className="ri-file-text-line me-2"></i> Page de bienvenue
                        </h6>
                        <Row className="g-3">
                          <Col md={12}>
                            <Form.Group controlId="titre">
                              <Form.Label className="form-label-modern">Titre de bienvenue</Form.Label>
                              <Form.Control
                                type="text"
                                value={bienvenueData.titre}
                                onChange={(e) => handleBienvenueChange('titre', e.target.value)}
                                className="form-control-modern"
                                placeholder="Titre de la page d'accueil"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="description">
                              <Form.Label className="form-label-modern">Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                value={bienvenueData.description}
                                onChange={(e) => handleBienvenueChange('description', e.target.value)}
                                rows={3}
                                className="form-control-modern"
                                placeholder="Description de l'église"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="vision">
                              <Form.Label className="form-label-modern">Vision</Form.Label>
                              <Form.Control
                                as="textarea"
                                value={bienvenueData.vision}
                                onChange={(e) => handleBienvenueChange('vision', e.target.value)}
                                rows={3}
                                className="form-control-modern"
                                placeholder="Vision de l'église"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group controlId="mission">
                              <Form.Label className="form-label-modern">Mission</Form.Label>
                              <div className="quill-wrapper">
                                <ReactQuill
                                  theme="snow"
                                  value={bienvenueData.mission}
                                  onChange={(content) => handleBienvenueChange('mission', content)}
                                  modules={quillModules}
                                  formats={quillFormats}
                                  placeholder="Décrivez la mission de l'église..."
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="action">
                              <Form.Label className="form-label-modern">Actions</Form.Label>
                              <div className="quill-wrapper">
                                <ReactQuill
                                  theme="snow"
                                  value={bienvenueData.action}
                                  onChange={(content) => handleBienvenueChange('action', content)}
                                  modules={quillModules}
                                  formats={quillFormats}
                                  placeholder="Actions principales de l'église..."
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="culte">
                              <Form.Label className="form-label-modern">Informations sur le culte</Form.Label>
                              <div className="quill-wrapper">
                                <ReactQuill
                                  theme="snow"
                                  value={bienvenueData.culte}
                                  onChange={(content) => handleBienvenueChange('culte', content)}
                                  modules={quillModules}
                                  formats={quillFormats}
                                  placeholder="Horaires et informations des cultes..."
                                />
                              </div>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="reference">
                              <Form.Label className="form-label-modern">Référence biblique</Form.Label>
                              <Form.Control
                                type="text"
                                value={bienvenueData.reference}
                                onChange={(e) => handleBienvenueChange('reference', e.target.value)}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="chapitre">
                              <Form.Label className="form-label-modern">Chapitre biblique</Form.Label>
                              <Form.Control
                                type="text"
                                value={bienvenueData.chapitre}
                                onChange={(e) => handleBienvenueChange('chapitre', e.target.value)}
                                className="form-control-modern"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Boutons de navigation */}
                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                      <div>
                        {currentStep > 1 && (
                          <Button
                            variant="light"
                            size="sm"
                            className="btn-modern-outline"
                            onClick={prevStep}
                          >
                            <i className="ri-arrow-left-line me-1"></i> Précédent
                          </Button>
                        )}
                      </div>
                      <div>
                        {currentStep < totalSteps ? (
                          <Button
                            variant="primary"
                            size="sm"
                            className="btn-modern-primary"
                            onClick={nextStep}
                          >
                            Suivant <i className="ri-arrow-right-line ms-1"></i>
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            size="sm"
                            className="btn-modern-success"
                            disabled={loading}
                          >
                            {loading ? (
                              <><Spinner size="sm" className="me-1" /> Mise à jour...</>
                            ) : (
                              <><i className="ri-check-line me-1"></i> Mettre à jour</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>
    </>
  );
}